import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useMemo, useState, type SubmitEventHandler } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  FieldError,
  Flex,
  MenuLinkButton,
  Select,
  Separator,
  Text,
  TextArea,
} from '@/components/common';
import { AccountSubpageShell } from '@/components/layout';
import { analyticsClient } from '@/lib';
import { supportService, type SupportCategory } from '@/services';

type Translate = (key: string) => string;

const CATEGORY_VALUES = [
  'bug',
  'safety',
  'suggestion',
  'other',
] as const satisfies readonly SupportCategory[];

const createHelpSchema = (t: Translate) =>
  z.object({
    category: z.string().min(1, t('help.errors.categoryRequired')),
    message: z.string().trim().min(1, t('help.errors.messageRequired')),
  });

type HelpFormValues = z.infer<ReturnType<typeof createHelpSchema>>;

export function HelpSettingsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const schema = useMemo(() => createHelpSchema(key => t(key)), [t]);
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HelpFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { category: '', message: '' },
  });

  const onValid: SubmitEventHandler<HTMLFormElement> = handleSubmit(async data => {
    setSubmitting(true);
    try {
      await supportService.submitMessage({
        category: data.category as SupportCategory,
        message: data.message,
      });
      analyticsClient.capture('support_message_submitted', { category: data.category });
      toast.success(t('help.success'));
      reset({ category: '', message: '' });
    } catch (err) {
      console.error('Failed to submit support message:', err);
      toast.error(t('help.error'));
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <AccountSubpageShell
      onBack={() => navigate('/account/help-legal')}
      title={t('help.title')}
      subtitle={t('help.subtitle')}
    >
      <MenuLinkButton
        to="/faq"
        title={t('help.faq.title')}
        subtitle={t('help.faq.subtitle')}
        external
        data-testid="help-faq-link"
      />
      <Separator size="4" my="5" />
      <Text
        size="1"
        color="gray"
        mb="3"
        style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        {t('help.contactHeading')}
      </Text>
      <Card>
        <form onSubmit={onValid} noValidate>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text
                size="1"
                color="gray"
                style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
              >
                {t('help.categoryLabel')}
              </Text>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select.Root value={field.value || undefined} onValueChange={field.onChange}>
                    <Select.Trigger placeholder={t('help.categoryPlaceholder')} />
                    <Select.Content>
                      {CATEGORY_VALUES.map(value => (
                        <Select.Item key={value} value={value}>
                          {t(`help.categories.${value}`)}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                )}
              />
              <FieldError message={errors.category?.message} />
            </Flex>

            <Flex direction="column" gap="1">
              <Text
                size="1"
                color="gray"
                style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}
              >
                {t('help.messageLabel')}
              </Text>
              <TextArea
                placeholder={t('help.messagePlaceholder')}
                rows={4}
                aria-invalid={errors.message ? true : undefined}
                {...register('message')}
              />
              <FieldError message={errors.message?.message} />
            </Flex>

            <Button
              type="submit"
              color="gray"
              highContrast
              fullWidth
              disabled={submitting}
              isLoading={submitting}
            >
              {t('help.send')}
            </Button>
          </Flex>
        </form>
      </Card>
    </AccountSubpageShell>
  );
}
